import React from 'react';
import PropTypes from 'prop-types';
import { CardListSkeleton, DataLoader } from 'components';
import './Timeline.scss';

/**
 * Timeline Component
 *
 * Generic vertical timeline with nodes on the left and custom content on the right.
 *
 * Props:
 * - items: array of data items
 * - getKey(item, index): optional function to return React key
 * - renderNodeContent(item, index): optional function to render content below the node (e.g. date range)
 * - renderCard(item, index): function to render the main card/content for each item
 * - className: optional wrapper class (defaults to 'work-history-timeline' to reuse styles)
 */
export const Timeline = ({
  isLoading = false,
  error = null,
  items,
  getKey = null,
  renderNodeContent = null,
  renderCard,
  className = 'work-history-timeline',
}) => {
  if (!items || (items.length === 0 && !isLoading)) return null;

  return (
    <DataLoader
      loading={isLoading}
      error={error}
      skeleton={<CardListSkeleton />}
    >
      <div className={className}>
        <div className='work-history-line' />
        {items.map((item, index) => {
          const key = getKey ? getKey(item, index) : index;

          return (
            <div key={key} className='work-history-item'>
              <div className='work-history-node-wrapper'>
                <div className='work-history-node' />
                <div className='work-history-node-date'>
                  {renderNodeContent && renderNodeContent(item, index)}
                </div>
              </div>

              {renderCard && renderCard(item, index)}
            </div>
          );
        })}
      </div>
    </DataLoader>
  );
};

Timeline.propTypes = {
  isLoading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
  getKey: PropTypes.func,
  renderNodeContent: PropTypes.func,
  renderCard: PropTypes.func.isRequired,
  className: PropTypes.string,
};
